import { useState } from 'react';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, GridRowEditStopReasons } from '@mui/x-data-grid';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CheckIcon from '@mui/icons-material/Check';

import { dataGridColumns } from '../../../utils/helper';

import { DataGridUI, DataGridIdentifiers, FormErrorUI, AutoCompleteSelectUI, AddButtonUi } from '../../Interface';

function EditToolbar(props) {
  const { setRowModesModel, disabled, push, defaultResourceFive } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    defaultResourceFive.id = uuidv4();
    await push(defaultResourceFive);

    setRowModesModel((prevModel) => ({
      ...prevModel,
      [defaultResourceFive.id]: { mode: GridRowModes.Edit },
    }));

    await validateField(`resourceFive.${values.resourceFive.length ?? 0}.resourceId`);
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
}

const ResourceFiveTable = ({ disabled, defaultResourceFive, autoCompleteOptions }) => {
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const [rowModesModel, setRowModesModel] = useState({});

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const getValueIndex = (values, id) => {
    return values.resourceFive.findIndex((rc) => rc.id === id);
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = async (id) => {
    const valueIndex = getValueIndex(values, id);

    await validateField(`resourceFive.${valueIndex}.resourceId`);
    if (errors?.resourceFive?.[valueIndex]?.resourceId) {
      return;
    }

    setRowModesModel((prevModel) => ({
      ...prevModel,
      [id]: { mode: GridRowModes.View },
    }));
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridColumns([
      {
        field: 'resourceId',
        headerName: 'Név',
        flex: 1.5,
        editable: true,
        renderCell: (data) => {
          const findedResourceOne = autoCompleteOptions?.resourceFive.find(
            (item) => item.value === data.row?.resourceId
          );
          return findedResourceOne?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedResourceOne = autoCompleteOptions.resourceFive.find((item) => item.value === data.row.resourceId);

          return (
            <Field
              name={`resourceFive.${valueIndex}.resourceId`}
              component={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors?.resourceFive?.[valueIndex]?.resourceId} />}
              selectedValue={values.resourceFive?.[valueIndex]?.resourceId}
              selectedLabelValue={findedResourceOne?.label}
              onChange={(_e, newVal) => {
                setFieldValue(`resourceFive.${valueIndex}.resourceId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal.value ?? null,
                });
              }}
              dataset={autoCompleteOptions.resourceFive}
              onBlur={async () => await validateField(`resourceFive.${valueIndex}.resourceId`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        flex: 1,
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
          if (isInEditMode) {
            return [
              <GridActionsCellItem
                icon={<CheckIcon />}
                label="Save"
                onClick={() => handleSaveClick(id)}
                color="inherit"
              />,
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => {
                  const valueIndex = getValueIndex(values, id);
                  return remove(valueIndex);
                }}
                color="inherit"
              />,
            ];
          }

          return [
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={handleEditClick(id)}
              color="inherit"
              disabled={disabled}
            />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="resourceFive">
            {({ remove, push }) => (
              <DataGridUI
                datagridIdentifier={DataGridIdentifiers.ResourceFiveTable}
                sx={{ height: 400 }}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.resourceFive ?? []}
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                slots={{
                  toolbar: EditToolbar,
                }}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defaultResourceFive,
                    setRowModesModel,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default ResourceFiveTable;
